import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { toast } from "react-toastify";

const baseURL = process.env.REACT_APP_BASEURL;

export const createShipment = createAsyncThunk(
    "shipment/createShipment",
    async (shipmentData, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem("token");
            const response = await axios.post(`${baseURL}/shipment`, shipmentData, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success("Shipment created successfully!");
            return response.data;
        } catch (error) {
            toast.error(error?.response?.data?.message || "Failed to create shipment");
            return rejectWithValue(error.response.data);
        }
    }
);

export const getShipmentList = createAsyncThunk(
    "shipment/getShipmentList",
    async ({ businessId, filters }, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem("token");
            const query = new URLSearchParams({ business: businessId, ...filters }).toString();
            const response = await axios.get(`${baseURL}/shipment?${query}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response.data);
        }
    }
);

export const getShipmentById = createAsyncThunk(
    "shipment/getShipmentById",
    async (id, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem("token");
            const response = await axios.get(`${baseURL}/shipment/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            return response.data;
        } catch (error) {
            toast.error("Failed to load shipment details");
            return rejectWithValue(error.response.data);
        }
    }
);

export const getShipmentByTrackingCode = createAsyncThunk(
    "shipment/getShipmentByTrackingCode",
    async (trackingCode, { rejectWithValue }) => {
        try {
            // Note: No auth header for public track endpoint
            const response = await axios.get(`${baseURL}/shipment/track/${trackingCode}`);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response.data);
        }
    }
);

export const updateShipmentStatus = createAsyncThunk(
    "shipment/updateShipmentStatus",
    async ({ id, statusData }, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem("token");
            const response = await axios.patch(`${baseURL}/shipment/${id}/status`, statusData, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success("Status updated successfully!");
            return response.data;
        } catch (error) {
            toast.error(error?.response?.data?.message || "Failed to update status");
            return rejectWithValue(error.response.data);
        }
    }
);

export const updateShipmentArrival = createAsyncThunk(
    "shipment/updateShipmentArrival",
    async ({ id, arrivalData }, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem("token");
            const response = await axios.patch(`${baseURL}/shipment/${id}/arrival`, arrivalData, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success("Estimated arrival updated");
            return response.data;
        } catch (error) {
            toast.error("Failed to update arrival");
            return rejectWithValue(error.response.data);
        }
    }
);

export const cancelShipment = createAsyncThunk(
    "shipment/cancelShipment",
    async (id, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem("token");
            const response = await axios.delete(`${baseURL}/shipment/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success("Shipment cancelled");
            return response.data;
        } catch (error) {
            toast.error("Failed to cancel shipment");
            return rejectWithValue(error.response.data);
        }
    }
);

const shipmentSlice = createSlice({
    name: "shipment",
    initialState: {
        shipmentList: [],
        currentShipment: null,
        publicTracking: null,
        loading: false,
        error: null,
        pagination: { page: 1, pages: 1, total: 0 }
    },
    reducers: {
        clearPublicTracking: (state) => {
            state.publicTracking = null;
        }
    },
    extraReducers: (builder) => {
        builder
            // List
            .addCase(getShipmentList.pending, (state) => { state.loading = true; })
            .addCase(getShipmentList.fulfilled, (state, action) => {
                state.loading = false;
                state.shipmentList = action.payload.data.list;
                state.pagination = action.payload.data.pagination;
            })
            .addCase(getShipmentList.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // GET By Id
            .addCase(getShipmentById.pending, (state) => { state.loading = true; })
            .addCase(getShipmentById.fulfilled, (state, action) => {
                state.loading = false;
                state.currentShipment = action.payload.data;
            })
            .addCase(getShipmentById.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // Public Tracking
            .addCase(getShipmentByTrackingCode.pending, (state) => { state.loading = true; state.error = null; })
            .addCase(getShipmentByTrackingCode.fulfilled, (state, action) => {
                state.loading = false;
                state.publicTracking = action.payload.data;
            })
            .addCase(getShipmentByTrackingCode.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
                state.publicTracking = null;
            })
            // Status Update
            .addCase(updateShipmentStatus.fulfilled, (state, action) => {
                state.currentShipment = action.payload.data;
            })
            // Arrival Update
            .addCase(updateShipmentArrival.fulfilled, (state, action) => {
                state.currentShipment = action.payload.data;
            })
            // Cancellation
            .addCase(cancelShipment.fulfilled, (state, action) => {
                state.currentShipment = action.payload.data;
            });
    }
});

export const { clearPublicTracking } = shipmentSlice.actions;
export default shipmentSlice.reducer;
